import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@elkaso-app/web-design';
import { useGetSourceBrandLocationByIdApi } from 'apis/crm-source-brand-locations/use-get-source-brand-location-by-id-api';
import React from 'react';
import { FiCalendar, FiGlobe, FiMapPin, FiPhone, FiStar, FiUser } from 'react-icons/fi';
import { ReadOnlyMap } from '../maps';

interface ViewLocationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: number | null;
}

const ViewLocationDetailsModal: React.FC<ViewLocationDetailsModalProps> = ({ isOpen, onClose, locationId }) => {
  const {
    data: locationDetails,
    isLoading,
    error,
  } = useGetSourceBrandLocationByIdApi(locationId as number, {
    enabled: isOpen && !!locationId,
  });

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size='xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <VStack spacing={4} py={8}>
              <Spinner size='lg' />
              <Text>Loading location details...</Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (error || !locationDetails) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size='md'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Error</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status='error'>
              <AlertIcon />
              Failed to load location details. Please try again.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='4xl'>
      <ModalOverlay />
      <ModalContent maxW='1000px'>
        <ModalHeader>
          <VStack align='start' spacing={2}>
            <HStack spacing={3}>
              <FiMapPin color='#3182CE' size={20} />
              <Text fontSize='xl' fontWeight='bold'>
                {locationDetails.name}
              </Text>
              {locationDetails.parentId && (
                <Badge colorScheme='orange' variant='subtle'>
                  Child Location
                </Badge>
              )}
            </HStack>
            <HStack spacing={2}>
              <Badge colorScheme='blue' variant='solid'>
                {locationDetails.sourcePlatform}
              </Badge>
              <Text fontSize='sm' color='gray.600'>
                ID: {locationDetails.sourceExternalId}
              </Text>
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align='stretch'>
            {/* Brand Information */}
            <Box>
              <Text fontSize='lg' fontWeight='semibold' mb={3} color='gray.700'>
                Brand Information
              </Text>
              <Box p={4} border='1px' borderColor='gray.200' borderRadius='md' bg='gray.50'>
                <HStack spacing={4}>
                  {locationDetails.brand.image && (
                    <div style={{ width: '60px', height: '60px', borderRadius: '8px', overflow: 'hidden' }}>
                      <img
                        src={locationDetails.brand.image}
                        alt={locationDetails.brand.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <VStack align='start' spacing={1}>
                    <Text fontWeight='semibold' fontSize='lg'>
                      {locationDetails.brand.name}
                    </Text>
                    <Badge colorScheme='gray' variant='subtle'>
                      {locationDetails.brand.countryCode}
                    </Badge>
                  </VStack>
                </HStack>
              </Box>
            </Box>

            <Divider />

            {/* Location Details */}
            <Box>
              <Text fontSize='lg' fontWeight='semibold' mb={3} color='gray.700'>
                Location Details
              </Text>
              <VStack spacing={3} align='stretch'>
                {locationDetails.address && (
                  <HStack spacing={3}>
                    <FiMapPin color='#4A5568' />
                    <Text>{locationDetails.address}</Text>
                  </HStack>
                )}

                {locationDetails.geoLocation && (
                  <HStack spacing={3}>
                    <FiMapPin color='#4A5568' />
                    <Text>
                      <strong>Geo Location:</strong> {locationDetails.geoLocation.name}
                    </Text>
                  </HStack>
                )}

                {locationDetails.coordinates?.lat && locationDetails.coordinates?.lng && (
                  <VStack spacing={3} align='stretch'>
                    <HStack spacing={3}>
                      <FiMapPin color='#4A5568' />
                      <Text>
                        <strong>Coordinates:</strong> {locationDetails.coordinates.lat},{' '}
                        {locationDetails.coordinates.lng}
                      </Text>
                    </HStack>

                    <Box>
                      <Text fontSize='sm' fontWeight='semibold' mb={2} color='gray.700'>
                        Location on Map
                      </Text>
                      <ReadOnlyMap
                        lat={locationDetails.coordinates.lat}
                        lng={locationDetails.coordinates.lng}
                        title={locationDetails.name}
                        height='250px'
                        zoom={16}
                      />
                    </Box>
                  </VStack>
                )}
              </VStack>
            </Box>

            <Divider />

            {/* Contact Information */}
            <Box>
              <Text fontSize='lg' fontWeight='semibold' mb={3} color='gray.700'>
                Contact Information
              </Text>
              <VStack spacing={3} align='stretch'>
                {locationDetails.phoneNumber && (
                  <HStack spacing={3}>
                    <FiPhone color='#4A5568' />
                    <Text>{locationDetails.phoneNumber}</Text>
                  </HStack>
                )}

                {locationDetails.website && (
                  <HStack spacing={3}>
                    <FiGlobe color='#4A5568' />
                    <Text color='blue.600' isTruncated>
                      {locationDetails.website}
                    </Text>
                  </HStack>
                )}

                {locationDetails.sourceUrl && (
                  <HStack spacing={3}>
                    <FiGlobe color='#4A5568' />
                    <Text>
                      <strong>Source URL:</strong>
                      <Text as='span' color='blue.600' ml={2} isTruncated>
                        {locationDetails.sourceUrl}
                      </Text>
                    </Text>
                  </HStack>
                )}
              </VStack>
            </Box>

            <Divider />

            {/* Ratings & Reviews */}
            {(locationDetails.rating || locationDetails.reviewsTotal || locationDetails.businessStatus) && (
              <>
                <Box>
                  <Text fontSize='lg' fontWeight='semibold' mb={3} color='gray.700'>
                    Ratings & Reviews
                  </Text>
                  <VStack spacing={3} align='stretch'>
                    {locationDetails.rating && typeof locationDetails.rating === 'number' && (
                      <HStack spacing={3}>
                        <FiStar color='#ECC94B' />
                        <Text>
                          <strong>Rating:</strong> {locationDetails.rating.toFixed(1)}/5
                          {locationDetails.ratingsTotal && (
                            <Text as='span' color='gray.500' ml={2}>
                              ({locationDetails.ratingsTotal} ratings)
                            </Text>
                          )}
                        </Text>
                      </HStack>
                    )}

                    {locationDetails.reviewsTotal && (
                      <HStack spacing={3}>
                        <FiUser color='#4A5568' />
                        <Text>
                          <strong>Reviews:</strong> {locationDetails.reviewsTotal} total reviews
                        </Text>
                      </HStack>
                    )}

                    {locationDetails.businessStatus && (
                      <HStack spacing={3}>
                        <Text>
                          <strong>Status:</strong>
                        </Text>
                        <Badge
                          colorScheme={locationDetails.businessStatus === 'OPERATIONAL' ? 'green' : 'red'}
                          variant='subtle'>
                          {locationDetails.businessStatus}
                        </Badge>
                      </HStack>
                    )}
                  </VStack>
                </Box>
                <Divider />
              </>
            )}

            {/* Pricing Information */}
            {(locationDetails.avgCostPerCouple || locationDetails.priceLevel) && (
              <>
                <Box>
                  <Text fontSize='lg' fontWeight='semibold' mb={3} color='gray.700'>
                    Pricing Information
                  </Text>
                  <VStack spacing={3} align='stretch'>
                    {locationDetails.avgCostPerCouple && (
                      <Text>
                        <strong>Average Cost per Couple:</strong> {locationDetails.avgCostPerCouple}{' '}
                        {locationDetails.avgCostCurrency || 'AED'}
                      </Text>
                    )}

                    {locationDetails.priceLevel && (
                      <Text>
                        <strong>Price Level:</strong>
                        <Badge colorScheme='green' variant='subtle' ml={2}>
                          {'$'.repeat(locationDetails.priceLevel)}
                        </Badge>
                      </Text>
                    )}
                  </VStack>
                </Box>
                <Divider />
              </>
            )}

            {/* Timestamps */}
            <Box>
              <Text fontSize='lg' fontWeight='semibold' mb={3} color='gray.700'>
                Record Information
              </Text>
              <VStack spacing={3} align='stretch'>
                <HStack spacing={3}>
                  <FiCalendar color='#4A5568' />
                  <Text>
                    <strong>Created:</strong> {new Date(locationDetails.createdAt).toLocaleString()}
                  </Text>
                </HStack>
                <HStack spacing={3}>
                  <FiCalendar color='#4A5568' />
                  <Text>
                    <strong>Last Updated:</strong> {new Date(locationDetails.updatedAt).toLocaleString()}
                  </Text>
                </HStack>
                {locationDetails.parentId && (
                  <Text>
                    <strong>Parent Location ID:</strong> {locationDetails.parentId}
                  </Text>
                )}
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ViewLocationDetailsModal;
